import { UfoAdventureGame } from '../templates/ufo-adventure/UfoAdventureGame';
import { LaneRallyGame } from '../templates/lane-rally/LaneRallyGame';
import { StationRunGame } from '../templates/station-run/StationRunGame';
import { draftToStationRunConfig } from '../templates/station-run/domain/stationLevels';
import { draftToRallyConfig } from '../templates/lane-rally/domain/rallyLevels';
import { draftToUfoConfig } from '../templates/ufo-adventure/domain/ufoLevels';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { DRAFT_FILE_MAX_BYTES, importDraftFile, sampleDraftFile } from './draftImport';
import {
  DRAFT_LIMIT_REACHED_MESSAGE,
  MAX_DRAFTS_PER_USER,
  type DraftSummary,
  type DraftInput,
  type GameDraft,
} from '../../shared/drafts';
import { templateName, type TemplateKey } from '../../shared/templates';
import { deleteDraft as deleteDraftRequest, getDraft, listDrafts } from './draftApi';
import { TrainEditor } from './TrainEditor';
import { PublicationEditor } from '../publication/PublicationEditor';
import { ResultCorrectionReview } from '../publication/ResultCorrectionReview';
import { getEditablePublication, type EditablePublication } from '../publication/publicationApi';
import { PhaserGame } from '../game/PhaserGame';
import { draftToTrainLevels } from '../game/domain/draftToTrainLevels';
import {
  publicationDurationKeys,
  publicationDurationLabels,
  type CreatorResourceUsage,
  type PublicationDurationKey,
  type PublicationSummary,
} from '../../shared/publications';
import type { BalanceArenaQuestionResult, TeacherResult, TeacherResultGroup } from '../../shared/results';
import {
  listPublications,
  listTeacherResults,
  listTeacherResultGroups,
  publishDraft,
  unpublishPublication,
} from '../publication/publicationApi';
import { ApiRequestError } from '../auth/authApi';
import { AdminPanel } from '../admin/AdminPanel';
import { draftToFieryRouteLevels } from '../templates/fiery-route/domain/routeLevels';
import { AlevliRotaGame } from '../templates/fiery-route/AlevliRotaGame';
import { fieryRouteModeOf } from '../../shared/fieryRouteModes';
import { clientTemplateRegistry } from '../templates/templateRegistry';
import { OrbitalBridgeGame } from '../templates/orbital-bridge/OrbitalBridgeGame';
import { draftToOrbitalBridgeConfig } from '../templates/orbital-bridge/domain/bridgeLevels';
import { TurboDuelGame } from '../templates/turbo-duel/TurboDuelGame';
import { draftToTurboDuelConfig } from '../templates/turbo-duel/domain/raceLevels';
import { BalanceArenaGame } from '../templates/balance-arena/BalanceArenaGame';
import { draftToBalanceArenaConfig } from '../templates/balance-arena/domain/balanceLevels';
import { BasketLearningGame } from '../templates/basket-learning/BasketLearningGame';
import { draftToBasketLearningConfig } from '../templates/basket-learning/domain/basketLevels';
import { GoalArenaGame } from '../templates/goal-arena/GoalArenaGame';
import { draftToGoalArenaConfig } from '../templates/goal-arena/domain/goalLevels';
import { BillionaireQuizGame } from '../templates/billionaire-quiz/BillionaireQuizGame';
import { draftToBillionaireQuizConfig } from '../templates/billionaire-quiz/domain/billionaireLevels';
import { formatTurkishLira } from '../../shared/billionaire';

type CreatorDashboardProps = { initialCreate?: boolean; initialTemplateKey?: TemplateKey; isAdmin?: boolean };


/** Düşene kadar tekrar: soru bazında kaç kez soruldu ve kaç kez doğru cevaplandı. */
function balanceQuestionStats(questions: BalanceArenaQuestionResult[]) {
  const stats = new Map<number, { level: number; prompt: string; asked: number; correct: number; wrong: number }>();
  for (const question of questions) {
    const stat = stats.get(question.level) ?? { level: question.level, prompt: question.questionPrompt, asked: 0, correct: 0, wrong: 0 };
    stat.asked += 1; if (question.correct) stat.correct += 1; else stat.wrong += 1;
    stats.set(question.level, stat);
  }
  return [...stats.values()].sort((a, b) => b.wrong - a.wrong || a.level - b.level);
}

export function CreatorDashboard({ initialCreate = false, initialTemplateKey = 'train-adventure', isAdmin = false }: CreatorDashboardProps) {
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [editorDraft, setEditorDraft] = useState<GameDraft | 'new' | { imported: DraftInput } | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const importInput = useRef<HTMLInputElement>(null);
  const initialCreateHandled = useRef(!initialCreate);
  const [newTemplateKey, setNewTemplateKey] = useState<TemplateKey>(initialTemplateKey);
  const [previewDraft, setPreviewDraft] = useState<GameDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [publications, setPublications] = useState<PublicationSummary[]>([]);
  const [publishingDraft, setPublishingDraft] = useState<DraftSummary | null>(null);
  const [publicationDuration, setPublicationDuration] = useState<PublicationDurationKey>('2d');
  const [publishing, setPublishing] = useState(false);
  const [resourceUsage, setResourceUsage] = useState<CreatorResourceUsage | null>(null);
  const [resultGroups, setResultGroups] = useState<TeacherResultGroup[]>([]);
  const [openResultGroups, setOpenResultGroups] = useState<Set<string>>(new Set());
  const [resultsByPublication, setResultsByPublication] = useState<Record<string, TeacherResult[]>>({});
  const [loadingResultGroup, setLoadingResultGroup] = useState<string | null>(null);
  const [publicationToUnpublish, setPublicationToUnpublish] = useState<PublicationSummary | null>(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<DraftSummary | null>(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [editingPublication, setEditingPublication] = useState<EditablePublication | null>(null);
  const [openingPublication, setOpeningPublication] = useState<string | null>(null);

  const formatStorage = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${Math.max(0, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB`;
  };
  const formatDuration = (durationMs: number) => {
    const seconds = Math.round(durationMs / 1000);
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  };

  const refresh = async () => {
    try {
      const [nextDrafts, publicationData, resultData] = await Promise.all([
        listDrafts(),
        listPublications(),
        listTeacherResultGroups(),
      ]);
      setDrafts(nextDrafts);
      setPublications(publicationData.publications);
      setResourceUsage(publicationData.usage);
      setResultGroups(resultData.groups);
      if (!initialCreateHandled.current) {
        initialCreateHandled.current = true;
        if (nextDrafts.length >= MAX_DRAFTS_PER_USER) {
          setMessage(DRAFT_LIMIT_REACHED_MESSAGE);
        } else {
          setEditorDraft('new');
        }
      }
    } catch {
      setMessage('Taslaklar şu anda yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    if (!publishingDraft) return;
    setPublishing(true);
    setMessage('Yayın bağlantısı hazırlanıyor…');
    try {
      const publication = await publishDraft(publishingDraft.id, publicationDuration);
      setPublications((current) => [publication, ...current]);
      void refresh();
      setPublishingDraft(null);
      setMessage('Oyun yayımlandı. Bağlantıyı aktif yayınlar bölümünden paylaşabilirsin.');
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Oyun yayımlanamadı.');
    } finally {
      setPublishing(false);
    }
  };

  const copyPublicationLink = async (publication: PublicationSummary) => {
    const url = new URL(publication.url, window.location.origin).href;
    try {
      await navigator.clipboard.writeText(url);
      setMessage('Yayın bağlantısı panoya kopyalandı.');
    } catch {
      setMessage(`Bağlantı: ${url}`);
    }
  };

  const unpublish = async () => {
    if (!publicationToUnpublish) return;
    setUnpublishing(true);
    try {
      await unpublishPublication(publicationToUnpublish.id);
      setPublications((current) => current.filter((publication) => publication.id !== publicationToUnpublish.id));
      setPublicationToUnpublish(null);
      void refresh();
      setMessage('Oyun yayından kaldırıldı. Eski bağlantı artık açılmaz.');
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Oyun yayından kaldırılamadı.');
    } finally {
      setUnpublishing(false);
    }
  };

  /** Dosyadan içe aktarma: dosya tarayıcıda okunur ve doğrulanır; editör içi dolu yeni taslak olarak açılır. */
  const importDraft = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImportErrors([]);
    if (drafts.length >= MAX_DRAFTS_PER_USER) { setMessage(DRAFT_LIMIT_REACHED_MESSAGE); return; }
    if (file.size > DRAFT_FILE_MAX_BYTES) { setImportErrors([`Dosya çok büyük (en fazla ${DRAFT_FILE_MAX_BYTES / 1024} KB).`]); return; }
    const result = importDraftFile(await file.text());
    if (!result.ok) { setImportErrors(result.errors); setMessage(''); return; }
    setMessage(''); setNewTemplateKey(result.draft.templateKey); setEditorDraft({ imported: result.draft });
  };

  const openNewDraft = () => {
    if (loading) {
      setMessage('Taslak sayınız kontrol ediliyor…');
      return;
    }
    if (drafts.length >= MAX_DRAFTS_PER_USER) {
      setMessage(DRAFT_LIMIT_REACHED_MESSAGE);
      return;
    }
    setMessage('');
    setEditorDraft('new');
  };

  const removeDraft = async () => {
    if (!draftToDelete) return;
    setDeletingDraft(true);
    try {
      await deleteDraftRequest(draftToDelete.id);
      setDrafts((current) => current.filter((draft) => draft.id !== draftToDelete.id));
      setPublishingDraft((current) => current?.id === draftToDelete.id ? null : current);
      setDraftToDelete(null);
      setMessage('Taslak ve taslağa ait görseller silindi.');
      void refresh();
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Taslak silinemedi.');
    } finally {
      setDeletingDraft(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openDraft = async (draftId: string) => {
    setMessage('Taslak açılıyor…');
    try {
      setEditorDraft(await getDraft(draftId));
      setMessage('');
    } catch {
      setMessage('Taslak açılamadı.');
    }
  };

  const openPublicationEditor = async (id: string) => {
    setOpeningPublication(id); setMessage('Yayındaki içerik açılıyor…');
    try { setEditingPublication(await getEditablePublication(id)); setMessage(''); }
    catch (error) { setMessage(error instanceof ApiRequestError ? error.message : 'Yayın açılamadı.'); }
    finally { setOpeningPublication(null); }
  };

  const preview = async (draftId: string) => {
    setMessage('Oyun önizlemesi hazırlanıyor…');
    try {
      setPreviewDraft(await getDraft(draftId));
      setMessage('');
    } catch {
      setMessage('Oyun önizlemesi açılamadı.');
    }
  };

  const toggleResultGroup = async (publicationId: string) => {
    const willOpen = !openResultGroups.has(publicationId);
    setOpenResultGroups((current) => {
      const next = new Set(current);
      if (willOpen) next.add(publicationId); else next.delete(publicationId);
      return next;
    });
    if (!willOpen || resultsByPublication[publicationId]) return;
    setLoadingResultGroup(publicationId);
    try {
      const data = await listTeacherResults(0, publicationId);
      setResultsByPublication((current) => ({ ...current, [publicationId]: data.results }));
    } finally {
      setLoadingResultGroup(null);
    }
  };

  if (previewDraft) {
    const isRoute = previewDraft.templateKey === 'fiery-route';
    const isBridge = previewDraft.templateKey === 'orbital-bridge';
    const isRace = previewDraft.templateKey === 'turbo-duel';
    const isBalance = previewDraft.templateKey === 'balance-arena';
    const isBasket = previewDraft.templateKey === 'basket-learning';
    const isGoal = previewDraft.templateKey === 'goal-arena';
    const isBillionaire = previewDraft.templateKey === 'billionaire-quiz';
    return (
      <section className="creator-area preview-area" id="creator-area" aria-labelledby="preview-title">
        <div className="creator-title-row">
          <div>
            <p className="eyebrow">Yalnızca sana açık taslak önizlemesi</p>
            <h2 id="preview-title">{previewDraft.title}</h2>
            <p>Bu oyun henüz yayımlanmadı. Kaydettiğin sorularla baştan sona deneyebilirsin.</p>
          </div>
          <button className="ghost-button" type="button" onClick={() => setPreviewDraft(null)}>Panele dön</button>
        </div>
        <div className="preview-game">
          {previewDraft.templateKey === 'station-run' ? <StationRunGame config={draftToStationRunConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} /> : previewDraft.templateKey === 'lane-rally' ? <LaneRallyGame config={draftToRallyConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} /> : previewDraft.templateKey === 'ufo-adventure' ? <UfoAdventureGame config={draftToUfoConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} /> : isBillionaire
            ? <BillionaireQuizGame config={draftToBillionaireQuizConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isGoal
            ? <GoalArenaGame config={draftToGoalArenaConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isBasket
            ? <BasketLearningGame config={draftToBasketLearningConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isBalance
            ? <BalanceArenaGame config={draftToBalanceArenaConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isRace
            ? <TurboDuelGame config={draftToTurboDuelConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isBridge
            ? <OrbitalBridgeGame config={draftToOrbitalBridgeConfig(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />
            : isRoute
            ? <AlevliRotaGame levels={draftToFieryRouteLevels(previewDraft)} mode={fieryRouteModeOf(previewDraft.bridgeMode)} label={`${previewDraft.title} taslak önizlemesi`} />
            : <PhaserGame levels={draftToTrainLevels(previewDraft)} label={`${previewDraft.title} taslak önizlemesi`} />}
        </div>
      </section>
    );
  }

  if (editingPublication) return <PublicationEditor initial={editingPublication} onCancel={() => setEditingPublication(null)} onSaved={() => {
    setEditingPublication(null); setResultsByPublication({}); setOpenResultGroups(new Set());
    setMessage('Yayın güncellendi. Bağlantı aynı kaldı; geçmiş sonuçlara düzeltme uygulandı.'); void refresh();
  }} />;

  if (editorDraft) {
    return (
      <TrainEditor
        initialDraft={editorDraft === 'new' || 'imported' in editorDraft ? undefined : editorDraft}
        importedDraft={editorDraft !== 'new' && 'imported' in editorDraft ? editorDraft.imported : undefined}
        templateKey={editorDraft === 'new' ? newTemplateKey : 'imported' in editorDraft ? editorDraft.imported.templateKey : editorDraft.templateKey}
        mediaUploadAllowed={Boolean(resourceUsage?.mediaUploads.allowed)}
        mediaUploadMessage={resourceUsage?.mediaUploads.message}
        onCancel={() => setEditorDraft(null)}
        onSaved={(saved) => {
          setEditorDraft(saved);
          void refresh();
        }}
      />
    );
  }

  return (
    <section className="creator-area" id="creator-area" aria-labelledby="creator-title">
      <div className="creator-title-row">
        <div>
          <p className="eyebrow">Öğretmen paneli</p>
          <h2 id="creator-title">Oyun taslaklarım</h2>
          <p>Şablonunu seç, sorularını hazırla ve yayınlamadan önce dilediğin kadar dene.</p>
        </div>
        <div className="dashboard-new-actions">
          <select aria-label="Yeni oyun şablonu" value={newTemplateKey} onChange={(event) => setNewTemplateKey(event.target.value as TemplateKey)}>
            {clientTemplateRegistry.map((template) => <option value={template.key} key={template.key}>{template.name}</option>)}
          </select>
          <button className="primary-button" type="button" onClick={openNewDraft}>+ Yeni oyun oluştur</button>
          <button className="ghost-button" type="button" onClick={() => importInput.current?.click()} disabled={loading || drafts.length >= MAX_DRAFTS_PER_USER}
            title="Sorular hazır bir dosyadan taslak oluştur">Dosyadan içe aktar</button>
          <a className="ghost-button sample-file-link" download={`ornek-${newTemplateKey}.json`}
            href={`data:application/json;charset=utf-8,${encodeURIComponent(sampleDraftFile(newTemplateKey))}`}>Örnek dosya</a>
          <input ref={importInput} type="file" accept=".json,application/json" hidden onChange={(event) => void importDraft(event)} aria-label="Taslak dosyası seç" />
        </div>
      </div>
      {message && <p className="dashboard-message" role="status">{message}</p>}
      {importErrors.length > 0 && <div className="import-errors" role="alert">
        <strong>Dosya içe aktarılamadı. Şunları düzeltip yeniden dene:</strong>
        <ul>{importErrors.map((error) => <li key={error}>{error}</li>)}</ul>
        <button className="ghost-button" type="button" onClick={() => setImportErrors([])}>Kapat</button>
      </div>}
      {resourceUsage && (
        <section className="quota-summary" aria-labelledby="quota-summary-title">
          <div>
            <p className="eyebrow">Kullanım ve kotalar</p>
            <h3 id="quota-summary-title">Hesap kaynakları</h3>
          </div>
          <div className="quota-meters">
            <div>
              <span>Oyun taslakları</span>
              <strong>{resourceUsage.drafts} / {resourceUsage.limits.drafts}</strong>
              <progress value={resourceUsage.drafts} max={resourceUsage.limits.drafts} />
              {resourceUsage.drafts >= resourceUsage.limits.drafts && (
                <small className="quota-over" role="status">Yeni oyun oluşturmak için kullanmadığınız bir taslağı silin.</small>
              )}
            </div>
            <div>
              <span>Aktif yayın</span>
              <strong>{resourceUsage.activePublications} / {resourceUsage.limits.activePublications}</strong>
              <progress value={resourceUsage.activePublications} max={resourceUsage.limits.activePublications} />
            </div>
            <div>
              <span>Görsel depolama</span>
              <strong>{formatStorage(resourceUsage.totalMediaBytes)} / {formatStorage(resourceUsage.limits.mediaBytes)}</strong>
              <progress value={resourceUsage.totalMediaBytes} max={resourceUsage.limits.mediaBytes} />
              <small>Taslaklar ve yayın kopyaları dahildir. Süresi dolan yayınlar saatlik temizlikte kotadan düşer.</small>
              {!resourceUsage.mediaUploads.allowed && (
                <small className="quota-over" role="status">{resourceUsage.mediaUploads.message}</small>
              )}
            </div>
          </div>
        </section>
      )}
      {publishingDraft && (
        <section className="publish-panel" aria-labelledby="publish-panel-title">
          <div>
            <p className="eyebrow">Süreli yayın</p>
            <h3 id="publish-panel-title">{publishingDraft.title}</h3>
            <p>Yayımlanan oyun taslaktan bağımsız bir kopya olur. Sonraki düzenlemeler bu bağlantıyı değiştirmez.</p>
          </div>
          <label>
            Yayında kalma süresi
            <select value={publicationDuration} onChange={(event) => setPublicationDuration(event.target.value as PublicationDurationKey)}>
              {publicationDurationKeys.map((duration) => (
                <option value={duration} key={duration}>{publicationDurationLabels[duration]}</option>
              ))}
            </select>
          </label>
          <div className="publish-actions">
            <button className="ghost-button" type="button" onClick={() => setPublishingDraft(null)} disabled={publishing}>Vazgeç</button>
            <button className="primary-button" type="button" onClick={() => void publish()} disabled={publishing}>
              {publishing ? 'Yayımlanıyor…' : 'Yayınla'}
            </button>
          </div>
        </section>
      )}
      {loading ? (
        <p className="empty-drafts">Taslaklar yükleniyor…</p>
      ) : drafts.length === 0 ? (
        <div className="empty-drafts">
          <strong>Henüz bir taslağın yok.</strong>
          <span>İlk eğitsel etkinliğini birkaç dakikada hazırlayabilirsin.</span>
        </div>
      ) : (
        <div className="draft-grid">
          {drafts.map((draft) => (
            <article className="draft-card" key={draft.id}>
              <span className="draft-template">{templateName(draft.templateKey)}</span>
              <h3>{draft.title}</h3>
              <p>{draft.maxLevels} {['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(draft.templateKey) ? 'soru' : 'seviye'} · {draft.questionCount} soru · {draft.templateKey === 'orbital-bridge' ? 'Kesintisiz yolculuk' : draft.templateKey === 'turbo-duel' ? '3 araçlı yarış' : draft.templateKey === 'balance-arena' ? (draft.repeatUntilFall ? 'Düşene kadar tekrar' : 'Tek düşüş') : draft.templateKey === 'basket-learning' ? '3B salon finali' : draft.templateKey === 'goal-arena' ? '3B futbol finali' : draft.templateKey === 'ufo-adventure' ? 'Kesintisiz uzay keşfi' : draft.templateKey === 'lane-rally' ? '3B şerit yarışı' : draft.templateKey === 'station-run' ? '4 canlı istasyon koşusu' : draft.templateKey === 'billionaire-quiz' ? 'Para ve bonus yarışması' : 'Seviyelere özel'}</p>
              <div className="draft-actions">
                <button className="primary-button" type="button" onClick={() => void preview(draft.id)}>Önizle ve oyna</button>
                <button className="ghost-button" type="button" onClick={() => void openDraft(draft.id)}>Düzenle</button>
                <button className="ghost-button" type="button" onClick={() => setPublishingDraft(draft)}>Yayınla</button>
                <button className="danger-button" type="button" onClick={() => setDraftToDelete(draft)}>Taslağı sil</button>
              </div>
            </article>
          ))}
        </div>
      )}
      {publications.length > 0 && (
        <section className="publication-list" aria-labelledby="active-publications-title">
          <div>
            <p className="eyebrow">Bağlantıya özel</p>
            <h3 id="active-publications-title">Aktif yayınlar</h3>
          </div>
          {publications.map((publication) => (
            <article className="publication-row" key={publication.id}>
              <div>
                <strong>{publication.title}</strong>
                <small>Sürüm {publication.revision ?? 1}</small>
                <small>{new Date(publication.expiresAt).toLocaleString('tr-TR')} tarihine kadar açık</small>
              </div>
              <div className="draft-actions">
                <button className="ghost-button" type="button" disabled={openingPublication !== null} onClick={() => void openPublicationEditor(publication.id)}>
                  {openingPublication === publication.id ? 'Açılıyor…' : 'Yayını düzenle'}
                </button>
                <button className="danger-button" type="button" onClick={() => setPublicationToUnpublish(publication)}>
                  Yayından kaldır
                </button>
                <a className="ghost-button" href={publication.url} target="_blank" rel="noreferrer">Oyunu aç</a>
                <button className="ghost-button" type="button" onClick={() => void copyPublicationLink(publication)}>Bağlantıyı kopyala</button>
              </div>
            </article>
          ))}
        </section>
      )}
      {publicationToUnpublish && (
        <div className="dialog-backdrop" role="presentation">
          <section className="auth-dialog publication-confirmation" role="dialog" aria-modal="true" aria-labelledby="unpublish-title">
            <p className="eyebrow">Yayını kapat</p>
            <h2 id="unpublish-title">Bu oyunu yayından kaldırmak istediğine emin misin?</h2>
            <p>
              <strong>{publicationToUnpublish.title}</strong> bağlantısı hemen kapanır ve öğrenciler oyunu açamaz.
              Taslağın silinmez; tamamlanmış öğrenci sonuçları 7 gün boyunca panelinde kalır.
            </p>
            <div className="publish-actions">
              <button className="ghost-button" type="button" onClick={() => setPublicationToUnpublish(null)} disabled={unpublishing}>Vazgeç</button>
              <button className="danger-button" type="button" onClick={() => void unpublish()} disabled={unpublishing}>
                {unpublishing ? 'Yayından kaldırılıyor…' : 'Evet, yayından kaldır'}
              </button>
            </div>
          </section>
        </div>
      )}
      {draftToDelete && (
        <div className="dialog-backdrop" role="presentation">
          <section className="auth-dialog publication-confirmation" role="dialog" aria-modal="true" aria-labelledby="delete-draft-title" aria-describedby="delete-draft-description">
            <p className="eyebrow">Taslağı kalıcı olarak sil</p>
            <h2 id="delete-draft-title">Bu oyun taslağını silmek istediğinize emin misiniz?</h2>
            <p id="delete-draft-description">
              <strong>{draftToDelete.title}</strong> taslağındaki sorular, seçenekler ve yüklediğiniz taslak görselleri kalıcı olarak silinecektir.
              Bu işlem geri alınamaz.
            </p>
            <p>
              Daha önce oluşturulmuş aktif yayın bağlantıları ve kaydedilmiş öğrenci sonuçları etkilenmez.
            </p>
            <div className="publish-actions">
              <button className="ghost-button" type="button" onClick={() => setDraftToDelete(null)} disabled={deletingDraft} autoFocus>Vazgeç</button>
              <button className="danger-button" type="button" onClick={() => void removeDraft()} disabled={deletingDraft}>
                {deletingDraft ? 'Siliniyor…' : 'Evet, taslağı sil'}
              </button>
            </div>
          </section>
        </div>
      )}
      <section className="teacher-results" aria-labelledby="teacher-results-title">
        <div>
          <p className="eyebrow">Öğrenci takibi</p>
          <h3 id="teacher-results-title">Oyun sonuçları</h3>
          <p>Sonuçlar yayın kapandıktan sonra 7 gün boyunca burada arşivlenir.</p>
        </div>
        {resultGroups.length === 0 ? (
          <p className="empty-drafts">Henüz kaydedilmiş bir öğrenci sonucu yok.</p>
        ) : (
          <div className="result-group-list">
            {resultGroups.map((group) => {
              const open = openResultGroups.has(group.publicationId);
              const groupResults = resultsByPublication[group.publicationId] ?? [];
              return (
                <section className={`result-group${open ? ' open' : ''}`} key={group.publicationId}>
                  <button type="button" className="result-group-trigger" aria-expanded={open} onClick={() => void toggleResultGroup(group.publicationId)}>
                    <span aria-hidden="true">⌄</span>
                    <span><strong>{group.publicationTitle}</strong><small>{templateName(group.templateKey)} · {group.resultCount} öğrenci sonucu</small></span>
                    <time>{new Date(group.latestFinishedAt).toLocaleString('tr-TR')}</time>
                  </button>
                  {open && (
                    <div className="result-group-content">
                      {loadingResultGroup === group.publicationId ? <p>Sonuçlar yükleniyor…</p> : (
                        <div className="table-wrap"><table><thead><tr>
                          <th>Öğrenci</th><th>Durum</th><th>Puan</th><th>Doğru / yanlış</th><th>Deneme</th><th>Süre</th><th>Tarih</th><th>Ayrıntı</th>
                        </tr></thead><tbody>
                          {groupResults.map((result) => (
                            <tr key={result.id}>
                              <td>{result.firstName} {result.lastName}<small className="result-version">Oynanan sürüm {result.publicationRevision ?? 1}{result.correction ? ` · Değerlendirme ${result.gradingRevision ?? 1}` : ''}</small></td><td>{result.completed ? 'Tamamladı' : 'Tamamlayamadı'}</td>
                              <td>{result.templateKey === 'billionaire-quiz' ? formatTurkishLira(result.score) : result.score}</td><td>{result.correct} / {result.wrong}</td><td>{result.attempts}</td>
                              <td>{formatDuration(result.durationMs)}</td><td>{new Date(result.finishedAt).toLocaleString('tr-TR')}</td>
                              <td><ResultCorrectionReview result={result} />{result.details?.templateKey === 'fiery-route'
                                ? <details><summary>{result.details.mode === 'guided' ? 'Normal mod' : '3 Can modu'} · Soru dökümü</summary><ul className="route-result-detail-list">
                                    {result.details.questions.map((question) => <li key={question.level}>
                                      S{question.level}: {formatDuration(question.durationMs)} · {result.details?.templateKey === 'fiery-route' && result.details.mode === 'guided' ? (question.completed ? 'doğru' : 'yanlış · doğru kapı gösterildi') : `${question.wrongGateCount} yanlış geçit`} · {question.caughtCount} yakalanma
                                    </li>)}
                                  </ul></details>
                                : result.details?.templateKey === 'train-adventure'
                                  ? <details><summary>Soru dökümü</summary><ul className="route-result-detail-list">
                                      {result.details.questions.map((question) => <li key={`${question.level}-${question.question}`}>
                                        S{question.level} · V{question.question}: {formatDuration(question.durationMs)} · {question.wrongHitCount} yanlış vagon
                                      </li>)}
                                    </ul></details>
                                : result.details?.templateKey === 'orbital-bridge'
                                  ? <details><summary>Soru dökümü</summary><ul className="route-result-detail-list">
                                      {result.details.questions.map((question) => <li key={question.level}>
                                        S{question.level}: {formatDuration(question.durationMs)} · {question.outcome === 'correct' ? 'doğru' : question.outcome === 'timeout' ? 'süre doldu' : 'yanlış'} · {question.wrongChoiceCount} yanlış seçim · {question.timeoutCount} zaman aşımı · {question.livesLost} can kaybı · {question.scoreDelta >= 0 ? '+' : ''}{question.scoreDelta} puan
                                      </li>)}
                                    </ul></details>
                                  : result.details?.templateKey === 'turbo-duel'
                                    ? <details><summary>{result.details.finalPlace}. sıra · Soru dökümü</summary><ul className="route-result-detail-list">
                                        {result.details.questions.map((question) => <li key={question.level}>
                                          S{question.level}: {formatDuration(question.durationMs)} · {question.outcome === 'correct' ? 'doğru' : question.outcome === 'timeout' ? 'süre doldu' : 'yanlış'} · {question.placeAfterQuestion}. sıra · taban {question.baseScore} + hız {question.speedBonus} + seri {question.streakBonus} = {question.scoreDelta >= 0 ? '+' : ''}{question.scoreDelta} puan · bot farkları {question.botGaps.join(' / ')}
                                        </li>)}
                                      </ul></details>
                                  : result.details?.templateKey === 'balance-arena' && result.details.repeatUntilFall
                                    ? <details><summary>{result.details.timeLimitReached ? '5 dakika düşmedi' : 'Düştü'} · Dayanma {formatDuration(result.details.survivalDurationMs)} · {result.details.questions.length} cevap · {result.details.questions.at(-1)?.round ?? 1}. tur</summary>
                                      <p>{result.correct} doğru · {result.wrong} yanlış</p>
                                      <ul className="route-result-detail-list">
                                        {balanceQuestionStats(result.details.questions).map((stat) => <li key={stat.level}>
                                          S{stat.level}: {stat.prompt} · {stat.asked} kez soruldu · {stat.correct} doğru{stat.wrong ? ` · ${stat.wrong} yanlış` : ''}
                                        </li>)}
                                      </ul></details>
                                  : result.details?.templateKey === 'balance-arena'
                                    ? <details><summary>{result.completed ? 'Tamamlandı' : `${result.details.fellAtQuestion}. soruda düştü`} · Soru dökümü</summary><ul className="route-result-detail-list">
                                        {result.details.questions.map((question) => <li key={question.level}>
                                          S{question.level}: {question.questionPrompt} · {formatDuration(question.durationMs)} · {question.correct ? 'doğru' : `yanlış: ${question.selectedAnswer}; doğru: ${question.correctAnswer}`}
                                        </li>)}
                                        <li>Toplam dayanma: {formatDuration(result.details.survivalDurationMs)} · İlerleme: {result.completed ? `${result.details.questions.length}/${result.details.questions.length}` : `${result.details.questions.length}/${Math.max(result.details.questions.length + 1, result.details.fellAtQuestion ?? 1)}`}</li>
                                      </ul></details>
                                  : result.details?.templateKey === 'basket-learning'
                                    ? <details><summary>{result.details.maxStreak} en uzun seri · Soru dökümü</summary><ul className="route-result-detail-list">
                                        {result.details.questions.map((question) => <li key={question.level}>
                                          S{question.level}: {question.questionPrompt || 'Görsel soru'} · {formatDuration(question.durationMs)} · {question.correct ? `${question.shotAttempts ? `doğru · ${question.shotAttempts}. atışta basket` : 'doğru'} · +${question.scoreDelta} puan` : `yanlış: ${question.selectedAnswer}; doğru: ${question.correctAnswer}`} 
                                        </li>)}
                                      </ul></details>
                                  : result.details?.templateKey === 'goal-arena'
                                    ? <details><summary>{result.details.goals} gol · {result.details.maxStreak} en uzun seri · Soru dökümü</summary><ul className="route-result-detail-list">
                                        {result.details.questions.map((question) => <li key={question.level}>
                                          S{question.level}: {question.questionPrompt || 'Görsel soru'} · {formatDuration(question.durationMs)} · {question.correct ? `${question.shotAttempts ? `${question.shotAttempts}. şutta gol` : 'gol'} · +${question.scoreDelta} puan` : `${question.shotOutcome} · yanlış: ${question.selectedAnswer}; doğru: ${question.correctAnswer}`}
                                        </li>)}
                                      </ul></details>
                                  : result.details?.templateKey === 'station-run'
                                    ? <div className="result-question-list"><p><strong>{result.completed ? 'Son istasyona ulaştı' : result.details.questions.length === result.details.segments.length ? `${result.details.questions.length}. istasyonda canı bitti` : `${result.details.segments.length}. istasyona giderken canı bitti`}</strong> · {result.details.segments.reduce((t, g) => t + g.coins, 0)} altın · {result.details.segments.reduce((t, g) => t + g.hits, 0)} engel çarpması · {result.details.distanceMeters} m</p>{result.details.questions.map(q => <div key={q.level}>
                                      <strong>{q.level}. {q.questionPrompt}</strong>
                                      {q.questionMediaUrl && <img className="ufo-report-image" src={q.questionMediaUrl} alt="Soru görseli" />}
                                      <p>{q.correct ? '✓ Doğru' : '✕ Yanlış'} · Seçilen: {'ABCD'[q.selectedChoiceIndex]} · {q.selectedAnswer} · Doğru: {q.correctAnswer}</p>
                                      {q.selectedMediaUrl && <img className="ufo-report-image" src={q.selectedMediaUrl} alt="Seçilen cevap görseli" />}
                                      {q.correctMediaUrl && <img className="ufo-report-image" src={q.correctMediaUrl} alt="Doğru cevap görseli" />}
                                      <small>{formatDuration(q.durationMs)} düşünme · {q.scoreDelta > 0 ? `+${q.scoreDelta}` : q.scoreDelta} puan{q.livesLost ? ' · 1 can' : ''} · Toplam {q.scoreAfter} · {q.livesAfter} can</small>
                                    </div>)}</div>
                                  : result.details?.templateKey === 'lane-rally'
                                    ? <div className="result-question-list"><p><strong>Altın bonusu +{result.details.bonus.points}</strong> · {result.details.bonus.coins} altın, {result.details.bonus.obstacleHits} bariyer (puana etkisiz) · Bilgi puanı {result.score - result.details.bonus.points}</p>{result.details.questions.map(q => <div key={q.level}>
                                      <strong>{q.level}. {q.questionPrompt}</strong>
                                      {q.questionMediaUrl && <img className="ufo-report-image" src={q.questionMediaUrl} alt="Soru görseli" />}
                                      <p>{q.correct ? '✓ Doğru' : '✕ Yanlış'} · Seçilen şerit: {'ABCD'[q.selectedChoiceIndex]} · {q.selectedAnswer} · Doğru: {q.correctAnswer}</p>
                                      {q.selectedMediaUrl && <img className="ufo-report-image" src={q.selectedMediaUrl} alt="Seçilen cevap görseli" />}
                                      {q.correctMediaUrl && <img className="ufo-report-image" src={q.correctMediaUrl} alt="Doğru cevap görseli" />}
                                      <small>{formatDuration(q.durationMs)} · {q.decision === 'manual' ? 'Şerit değiştirdi' : 'Başladığı şeritte kaldı'} · +{q.scoreDelta} puan · Toplam {q.scoreAfter}</small>
                                    </div>)}</div>
                                  : result.details?.templateKey === 'ufo-adventure'
                                    ? <div className="result-question-list">{result.details.bonus && <p><strong>Keşif bonusu +{result.details.bonus.points}</strong> · {result.details.bonus.stars} yıldız, {result.details.bonus.asteroidHits} göktaşı · Bilgi puanı {result.score - result.details.bonus.points}</p>}{result.details.questions.map(q => <div key={q.level}>
                                      <strong>{q.level}. {q.questionPrompt}</strong>
                                      {q.questionMediaUrl && <img className="ufo-report-image" src={q.questionMediaUrl} alt="Soru görseli" />}
                                      <p>{q.correct ? '✓ Doğru' : '✕ Yanlış'} · Seçilen: {q.selectedAnswer} · Doğru: {q.correctAnswer}</p>
                                      {q.selectedMediaUrl && <img className="ufo-report-image" src={q.selectedMediaUrl} alt="Seçilen cevap görseli" />}
                                      {q.correctMediaUrl && <img className="ufo-report-image" src={q.correctMediaUrl} alt="Doğru cevap görseli" />}
                                      <small>{formatDuration(q.durationMs)} · {q.decision === 'manual' ? 'Öğrenci seçimi' : 'Otomatik geçiş'} · +{q.scoreDelta} puan · Toplam {q.scoreAfter}</small>
                                    </div>)}</div>
                                  : result.details?.templateKey === 'billionaire-quiz'
                                    ? <details><summary>{formatTurkishLira(result.details.finalBank)} final kasa · Soru ve bonus dökümü</summary><ul className="route-result-detail-list">
                                        {result.details.questions.map((question) => <li key={`q-${question.level}`}>
                                          S{question.level}: {question.questionPrompt || 'Görsel soru'} · {formatDuration(question.durationMs)} · {question.correct ? `doğru · ${formatTurkishLira(question.basePrize)}${question.appliedMultiplier > 1 ? ` × ${question.appliedMultiplier}` : ''} = +${formatTurkishLira(question.awardedAmount)}` : `yanlış: ${question.selectedAnswer}; doğru: ${question.correctAnswer}`} · kasa {formatTurkishLira(question.bankAfter)}
                                        </li>)}
                                        {result.details.bonuses.map((bonus) => <li key={`b-${bonus.round}`}>
                                          Bonus {bonus.round} · S{bonus.afterQuestion} sonrası · {bonus.effect} · {bonus.amountDelta >= 0 ? '+' : ''}{formatTurkishLira(bonus.amountDelta)} · kasa {formatTurkishLira(bonus.bankAfter)}{bonus.multiplierAfter > 1 ? ` · x${bonus.multiplierAfter} hazır` : ''}
                                        </li>)}
                                      </ul></details>
                                  : '—'}</td>
                            </tr>
                          ))}
                        </tbody></table></div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </section>
      {isAdmin && <AdminPanel onQuotaChanged={() => void refresh()} />}
    </section>
  );
}
